import React, { useState, useRef, useEffect } from 'react';
import { Camera, RefreshCw, Check, X, AlertCircle } from 'lucide-react';

interface SelfieCaptureProps {
  onCapture: (file: File) => void;
  onCancel: () => void;
}

export const SelfieCapture: React.FC<SelfieCaptureProps> = ({ onCapture, onCancel }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      setError('');
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
          setCameraReady(true);
        };
      }
    } catch (err: any) {
      setError(
        err.name === 'NotAllowedError'
          ? 'Camera access denied. Please allow camera permissions and try again.'
          : 'Unable to access camera. Please check your device settings.'
      );
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);
        stopCamera();
      }
    }, 'image/jpeg', 0.95);
  };

  const handleCountdownCapture = () => {
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          setTimeout(() => {
            capturePhoto();
            setCountdown(null);
          }, 100);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const retake = () => {
    setCapturedImage(null);
    startCamera();
  };

  const confirmCapture = () => {
    if (!canvasRef.current) return;

    canvasRef.current.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `selfie-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
      }
    }, 'image/jpeg', 0.95);
  };

  return (
    <div className="selfie-capture-overlay">
      <div className="selfie-capture-modal">
        <div className="selfie-capture-header">
          <h3>Take Your Selfie</h3>
          <button onClick={onCancel} className="selfie-close-btn" aria-label="Close">
            <X size={24} />
          </button>
        </div>

        <div className="selfie-capture-content">
          {error ? (
            <div className="selfie-error">
              <AlertCircle size={48} />
              <p>{error}</p>
              <button onClick={startCamera} className="btn btn-primary">
                Try Again
              </button>
            </div>
          ) : capturedImage ? (
            <div className="selfie-preview">
              <img src={capturedImage} alt="Captured selfie" />
            </div>
          ) : (
            <div className="selfie-camera-container">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="selfie-video"
              />

              {/* Face Overlay */}
              <div className="selfie-overlay-mask">
                <svg className="face-outline" viewBox="0 0 200 250">
                  <ellipse
                    cx="100"
                    cy="125"
                    rx="80"
                    ry="105"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeDasharray="8 4"
                  />
                </svg>
              </div>

              {/* Countdown */}
              {countdown !== null && (
                <div className="selfie-countdown">
                  {countdown}
                </div>
              )}

              {/* Instructions */}
              {cameraReady && !countdown && (
                <div className="selfie-instructions">
                  <p>Position your face within the oval</p>
                  <ul>
                    <li>Look directly at the camera</li>
                    <li>Ensure good lighting</li>
                    <li>Remove glasses if possible</li>
                    <li>Keep a neutral expression</li>
                  </ul>
                </div>
              )}
            </div>
          )}

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>

        <div className="selfie-capture-actions">
          {capturedImage ? (
            <>
              <button onClick={retake} className="btn btn-secondary">
                <RefreshCw size={20} />
                Retake
              </button>
              <button onClick={confirmCapture} className="btn btn-primary">
                <Check size={20} />
                Use This Photo
              </button>
            </>
          ) : (
            <>
              <button onClick={onCancel} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleCountdownCapture}
                className="btn btn-primary"
                disabled={!cameraReady || countdown !== null}
              >
                <Camera size={20} />
                {countdown !== null ? 'Capturing...' : 'Capture Photo'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
